import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from '@mui/icons-material';
import sessionService from '../api/sessionService';
import { useFavicon } from '../useFavicon';
import WizardStep1Upload from '../components/album-wizard/WizardStep1Upload';
import WizardStep2FaceRecognition from '../components/album-wizard/WizardStep2FaceRecognition';
import WizardStep3Analysis from '../components/album-wizard/WizardStep3Analysis';
import WizardStep4Clustering from '../components/album-wizard/WizardStep4Clustering';
import WizardStep5Story from '../components/album-wizard/WizardStep5Story';
import WizardStep6Layout from '../components/album-wizard/WizardStep6Layout';
import WizardStep6Preview from '../components/album-wizard/WizardStep6Preview';
import AiCostSummaryPanel from '../components/album-wizard/AiCostSummaryPanel';
import WizardNavBar from '../components/album-wizard/WizardNavBar';

const WIZARD_FAVICON_URL = process.env.PUBLIC_URL + '/album-icon.png';

// ─── Step color palette (one per step, matches AiCostSummaryPanel) ────────────
const STEP_COLORS = [
  '#1565c0', // Step 1 – Upload         (blue-900)
  '#6a1b9a', // Step 2 – Face Recognition (purple-900)
  '#00695c', // Step 3 – AI Analysis    (teal-800)
  '#37474f', // Step 4 – Clustering     (blue-grey-800)
  '#e65100', // Step 5 – Story          (deep-orange-900)
  '#558b2f', // Step 6 – Layout         (light-green-800)
  '#283593', // Step 7 – Preview        (indigo-900)
];

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 0,
    label: 'Upload Photos',
    shortLabel: 'Upload',
    description: 'Add photos from your device or Google Photos',
  },
  {
    id: 1,
    label: 'Face Recognition',
    shortLabel: 'Faces',
    description: 'Tag people in your photos so the album can feature them',
  },
  {
    id: 2,
    label: 'AI Image Analysis',
    shortLabel: 'Analysis',
    description: 'Analyse each photo and generate semantic embeddings',
  },
  {
    id: 3,
    label: 'Clustering',
    shortLabel: 'Clusters',
    description: 'Group similar photos into clusters for album sections',
  },
  {
    id: 4,
    label: 'Story / Timeline',
    shortLabel: 'Story',
    description: 'Build a narrative timeline or scene breakdown for your album',
  },
  {
    id: 5,
    label: 'AI Page Layout',
    shortLabel: 'Layout',
    description: 'AI designs A4 page layouts with artistic image placement per story scene',
  },
  {
    id: 6,
    label: 'Preview & Export',
    shortLabel: 'Preview',
    description: 'Review the full album spread by spread, then finish',
  },
];

// localStorage keys — used as fast initial value before the DB response arrives
const stepKey = (sessionId) => `album-wizard-step-${sessionId}`;
const maxStepKey = (sessionId) => `album-wizard-max-step-${sessionId}`;

/**
 * Derive the backend-operation status for a given wizard step from session data.
 * Returns 'in-progress' | 'completed' | 'failed' | 'cancelled' | null
 */
function getBackendStepStatus(stepId, session) {
  if (!session) return null;
  switch (stepId) {
    case 0: { // Upload Photos
      const uStats = session.stepStatus?.upload;
      if (uStats?.inProgress === true) {
        // Guard against stale flags (e.g. server restart mid-upload): ignore if > 30 min old
        const startedAt = uStats.inProgressStartedAt ? new Date(uStats.inProgressStartedAt) : null;
        const isRecent = startedAt && (Date.now() - startedAt.getTime()) < 30 * 60 * 1000;
        if (isRecent) return 'in-progress';
      }
      if ((session.imageStats?.totalImages ?? 0) > 0) return 'completed';
      return null;
    }
    case 1: { // Face Recognition
      const op = session.stepStatus?.faceRecognition;
      if (op?.status === 'in-progress') return 'in-progress';
      if (op?.status === 'completed') return 'completed';
      if (op?.status === 'failed') return 'failed';
      if (op?.status === 'cancelled') return 'cancelled';
      return null;
    }
    case 2: { // AI Image Analysis
      if (session.stepStatus?.embeddings?.inProgress) return 'in-progress';
      if (session.embeddingTokenStats) return 'completed';
      return null;
    }
    case 3: { // Clustering
      if (session.stepStatus?.clustering?.lastClustered) return 'completed';
      return null;
    }
    case 4: { // Story / Timeline
      const sp = session.stepStatus?.storyGeneration;
      if (sp?.status === 'in-progress') return 'in-progress';
      if (sp?.status === 'done' || session.storyTimeline) return 'completed';
      if (sp?.status === 'failed') return 'failed';
      return null;
    }
    case 5: { // AI Page Layout — backend persists 'running', not 'in-progress'
      const layoutStatus = session.stepStatus?.layoutGeneration;
      if (layoutStatus?.status === 'running') return 'in-progress';
      if (session.albumLayout?.layouts?.length > 0) return 'completed';
      if (layoutStatus?.status === 'failed') return 'failed';
      if (layoutStatus?.status === 'cancelled') return 'cancelled';
      return null;
    }
    default:
      return null;
  }
}

// ─── Placeholder for steps not yet implemented ────────────────────────────────
const StepPlaceholder = ({ step, onNext, onBack }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography variant="h6" gutterBottom>
      {step.label}
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      {step.description}
    </Typography>
    <Alert severity="info" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
      This step is coming soon. You can skip forward for now.
    </Alert>
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
      {onBack && (
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      )}
      <Button variant="contained" onClick={() => onNext({})}>
        {step.id === STEPS.length - 1 ? 'Finish' : 'Continue'}
      </Button>
    </Box>
  </Box>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const AlbumWizardPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  useFavicon(WIZARD_FAVICON_URL);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Start from localStorage immediately; will be reconciled with DB value after session loads
  const [activeStep, setActiveStep] = useState(() => {
    try {
      const saved = localStorage.getItem(stepKey(sessionId));
      return saved !== null ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  const [maxReachedStep, setMaxReachedStep] = useState(() => {
    try {
      const saved = localStorage.getItem(maxStepKey(sessionId));
      return saved !== null ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });
  // Guard: apply DB-persisted step only once on first session load
  const [dbStepApplied, setDbStepApplied] = useState(false);
  const [sessionPhotoCount, setSessionPhotoCount] = useState(0);
  const [costRefreshKey, setCostRefreshKey] = useState(0);
  const isWideLayoutStep = false;

  useEffect(() => {
    setMaxReachedStep((prev) => Math.max(prev, activeStep));
  }, [activeStep]);

  // ─── Load session ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await sessionService.getSession(sessionId);
        setSession(s);
        setSessionPhotoCount(s?.formattedStats?.totalImages || 0);
      } catch (err) {
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };
    if (sessionId) load();
  }, [sessionId]);

  // ─── Poll session while on the Upload step AND an upload is in progress ─────────────
  useEffect(() => {
    if (activeStep !== 0 || !sessionId) return;
    const uStats = session?.stepStatus?.upload;
    const isInProgress = uStats?.inProgress === true;
    if (!isInProgress) return; // upload not running — no need to poll
    const iv = setInterval(async () => {
      try {
        const s = await sessionService.getSession(sessionId);
        setSession(s);
        setSessionPhotoCount(s?.formattedStats?.totalImages || 0);
      } catch { /* ignore poll errors */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [activeStep, sessionId, session?.stepStatus?.upload?.inProgress]);

  // ─── Reconcile step with DB value (runs once after first session load) ───────
  useEffect(() => {
    if (!session || dbStepApplied) return;
    setDbStepApplied(true);
    const dbStep = session.wizardProgress?.currentStep;
    const dbMax = session.wizardProgress?.maxReachedStep;
    if (dbStep != null) {
      setActiveStep(dbStep);
      try { localStorage.setItem(stepKey(sessionId), String(dbStep)); } catch { /* ignore */ }
    }
    if (dbMax != null) {
      setMaxReachedStep((prev) => {
        const next = Math.max(prev, dbMax);
        try { localStorage.setItem(maxStepKey(sessionId), String(next)); } catch { /* ignore */ }
        return next;
      });
    }
  }, [session, dbStepApplied, sessionId]);

  // ─── Derive backend operation status per step for chip indicators ────────────
  const backendStepStatus = useMemo(() => {
    if (!session) return {};
    const map = {};
    [0, 1, 2, 3, 4, 5].forEach((sid) => {
      const s = getBackendStepStatus(sid, session);
      if (s) map[sid] = s;
    });
    return map;
  }, [session]);

  // ─── Persist step to localStorage + DB ──────────────────────────────────────
  const goToStep = useCallback(
    (step) => {
      const boundedStep = Math.max(0, Math.min(step, STEPS.length - 1));
      setActiveStep(boundedStep);
      let newMax = boundedStep;
      setMaxReachedStep((prev) => {
        newMax = Math.max(prev, boundedStep);
        try { localStorage.setItem(maxStepKey(sessionId), String(newMax)); } catch { /* ignore */ }
        return newMax;
      });
      try { localStorage.setItem(stepKey(sessionId), String(boundedStep)); } catch { /* ignore */ }
      // Persist to DB (fire-and-forget)
      sessionService.updateWizardProgress(sessionId, {
        currentStep: boundedStep,
        maxReachedStep: newMax,
      });
    },
    [sessionId]
  );

  const handleNext = useCallback(
    (_data) => {
      const next = activeStep + 1;
      if (next >= STEPS.length) {
        // Wizard complete – clear local storage and persist final state
        localStorage.removeItem(stepKey(sessionId));
        localStorage.removeItem(maxStepKey(sessionId));
        sessionService.updateWizardProgress(sessionId, { currentStep: 0, maxReachedStep: STEPS.length - 1 });
        navigate('/sessions');
      } else {
        goToStep(next);
      }
    },
    [activeStep, sessionId, navigate, goToStep]
  );

  const handleBack = useCallback(() => {
    if (activeStep > 0) goToStep(activeStep - 1);
  }, [activeStep, goToStep]);

  const handleStepClick = useCallback(
    (targetStep) => {
      if (targetStep <= maxReachedStep) {
        goToStep(targetStep);
      }
    },
    [goToStep, maxReachedStep]
  );

  const isStepCompleted = useCallback(
    (stepId) => stepId !== activeStep && stepId <= maxReachedStep,
    [activeStep, maxReachedStep]
  );
  const progressRailSidePx = 10;
  const railInsetPct = 100 / (STEPS.length * 2);

  // ── Nav config — updated by each step via onNavConfigChange ─────────────────
  const [navConfig, setNavConfig] = useState({
    nextLabel: 'Continue',
    nextDisabled: false,
    backDisabled: false,
    showSkip: false,
    skipLabel: 'Skip',
    onNextOverride: null,
    onSkipOverride: null,
  });
  const railProgressPct = STEPS.length > 1
    ? Math.round((activeStep / (STEPS.length - 1)) * 100)
    : 100;

  // ─── Refresh session (called after uploads) ─────────────────────────────────
  const handleSessionRefresh = useCallback(async () => {
    try {
      const updated = await sessionService.getSession(sessionId);
      setSession(updated);
      setSessionPhotoCount(updated?.formattedStats?.totalImages || 0);
    } catch { /* ignore */ }
  }, [sessionId]);

  // ─── Render step content ─────────────────────────────────────────────────────
  const renderStep = () => {
    const step = STEPS[activeStep];
    switch (activeStep) {
      case 0:
        return (
          <WizardStep1Upload
            session={session}
            onNext={handleNext}
            sessionPhotoCount={sessionPhotoCount}
            onSessionRefresh={handleSessionRefresh}
            onNavConfigChange={setNavConfig}
          />
        );
      case 1:
        return (
          <WizardStep2FaceRecognition
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onNavConfigChange={setNavConfig}
          />
        );
      case 2:
        return (
          <WizardStep3Analysis
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onRunComplete={() => setCostRefreshKey((k) => k + 1)}
            onNavConfigChange={setNavConfig}
          />
        );
      case 3:
        return (
          <WizardStep4Clustering
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onNavConfigChange={setNavConfig}
          />
        );
      case 4:
        return (
          <WizardStep5Story
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onRunComplete={() => setCostRefreshKey((k) => k + 1)}
            onNavConfigChange={setNavConfig}
          />
        );
      case 5:
        return (
          <WizardStep6Layout
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onNavConfigChange={setNavConfig}
          />
        );
      case 6:
        return (
          <WizardStep6Preview
            session={session}
            onNext={handleNext}
            onBack={handleBack}
            onNavConfigChange={setNavConfig}
          />
        );
      default:
        return (
          <StepPlaceholder
            step={step}
            onNext={handleNext}
            onBack={activeStep > 0 ? handleBack : null}
          />
        );
    }
  };

  // ─── Loading / error states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading session…
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sessions')}>
          Back to Sessions
        </Button>
      </Container>
    );
  }

  return (
    <Container
      disableGutters={isWideLayoutStep}
      maxWidth={isWideLayoutStep ? false : 'lg'}
      sx={{
        width: '100%',
        maxWidth: isWideLayoutStep ? 'none !important' : undefined,
        py: 4,
        px: isWideLayoutStep ? 0 : undefined,
        ...(isWideLayoutStep
          ? {
              width: { xs: '100%', lg: 'calc(100vw - 32px)' },
              ml: { xs: 0, lg: 'calc(50% - 50vw + 16px)' },
              mr: { xs: 0, lg: 0 },
            }
          : {}),
      }}
    >
      {/* ── Progress & Navigation section ─────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          borderWidth: 6,
          borderStyle: 'solid',
          borderColor: 'divider',
          px: { xs: 1.5, md: 2 },
          pt: { xs: 1.5, md: 2 },
          pb: 1.5,
          mb: 2,
          bgcolor: 'background.default',
        }}
      >
      {/* Title bar */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
        <Tooltip title="Back to Sessions">
          <IconButton onClick={() => navigate('/sessions')} size="small">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Typography
          variant="h5"
          component="h1"
          fontWeight={700}
          textAlign="center"
          noWrap
          title={`Album progress: ${session?.name || 'Untitled album'}.`}
        >
          Album progress: {session?.name || 'Untitled album'}.
        </Typography>

        <Tooltip title="Sessions Home">
          <IconButton onClick={() => navigate('/sessions')}>
            <HomeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Linear progress + step process row ───────────────────────────────── */}
      <Box
        sx={{
          mb: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mb: 1,
            px: `${progressRailSidePx}px`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${railInsetPct}%`,
              right: `${railInsetPct}%`,
              height: 6,
              borderRadius: 999,
              bgcolor: 'action.disabledBackground',
              transform: 'translateY(-50%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${railInsetPct}%`,
              height: 6,
              borderRadius: 999,
              width: `calc((100% - ${railInsetPct * 2}%) * ${railProgressPct / 100})`,
              maxWidth: `calc(100% - ${railInsetPct * 2}%)`,
              bgcolor: 'primary.main',
              transform: 'translateY(-50%)',
              transition: 'width 220ms ease',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))`,
              alignItems: 'center',
            }}
          >
            {STEPS.map((step) => {
              const done = isStepCompleted(step.id);
              const active = step.id === activeStep;
              return (
                <Box
                  key={`rail-${step.id}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    border: '2px solid',
                    borderColor: done || active ? 'primary.main' : 'divider',
                    bgcolor: done ? 'primary.main' : active ? 'background.paper' : 'background.paper',
                    color: done ? '#fff' : active ? 'primary.main' : 'text.disabled',
                    boxShadow: active ? '0 0 0 3px rgba(25, 118, 210, 0.18)' : 'none',
                  }}
                  >
                    {done ? '✓' : step.id + 1}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.8,
            px: { xs: 0.25, md: 0.5 },
          }}
        >
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
            Completed: {activeStep + 1}/{STEPS.length}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Remaining: {Math.max(0, STEPS.length - (activeStep + 1))}
          </Typography>
        </Box>

        <Box
          sx={{
            px: `${progressRailSidePx}px`,
            display: 'grid',
            gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))`,
            alignItems: 'start',
            columnGap: { xs: 0.25, md: 0.5 },
            rowGap: 0.5,
          }}
        >
        {STEPS.map((step) => {
          const color = STEP_COLORS[step.id] || '#1976d2';
          const isActive = step.id === activeStep;
          const isVisited = step.id <= maxReachedStep;
          const canNav = step.id !== activeStep && isVisited;
          const bStatus = backendStepStatus[step.id]; // 'in-progress'|'completed'|'failed'|'cancelled'|undefined
          // Build status icon (shown only for steps that have run a backend operation)
          let StatusIcon = null;
          if (!isActive && bStatus === 'in-progress') {
            StatusIcon = (
              <HourglassEmptyIcon
                sx={{ fontSize: 11, ml: 0.4, verticalAlign: 'middle',
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }}
              />
            );
          } else if (!isActive && bStatus === 'completed') {
            StatusIcon = <CheckCircleIcon sx={{ fontSize: 11, ml: 0.4, verticalAlign: 'middle' }} />;
          } else if (!isActive && (bStatus === 'failed' || bStatus === 'cancelled')) {
            StatusIcon = <ErrorOutlineIcon sx={{ fontSize: 11, ml: 0.4, verticalAlign: 'middle' }} />;
          }
          return (
            <Box key={step.id} sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <Box
                onClick={canNav ? () => handleStepClick(step.id) : undefined}
                role={canNav ? 'button' : undefined}
                tabIndex={canNav ? 0 : -1}
                sx={{
                  minHeight: 54,
                  width: { xs: '100%', sm: '94%', md: '92%' },
                  maxWidth: 116,
                  px: { xs: 0.75, md: 0.9 },
                  py: 0.8,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: isVisited ? color : 'divider',
                  bgcolor: isActive ? color : 'background.paper',
                  color: isActive ? '#fff' : isVisited ? color : 'text.disabled',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  boxShadow: isActive ? `0 4px 12px ${color}45` : 'none',
                  transform: isActive ? 'translateY(-1px)' : 'none',
                  transition: 'all 0.2s ease',
                  cursor: canNav ? 'pointer' : 'default',
                  '&:hover': canNav ? { bgcolor: `${color}14` } : {},
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700 }}>
                  Step {step.id + 1}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.76rem' } }}>
                  {step.shortLabel}
                  {StatusIcon}
                </Typography>
              </Box>

              {step.id < STEPS.length - 1 && (
                <ArrowForwardIosIcon
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: { xs: -6, sm: -7, md: -8 },
                    transform: 'translateY(-50%)',
                    fontSize: 14,
                    color: step.id < maxReachedStep ? STEP_COLORS[step.id + 1] : 'text.disabled',
                    zIndex: 2,
                  }}
                />
              )}
            </Box>
          );
        })}
        </Box>
      </Box>
      {/* Active step description */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textAlign: 'center' }}>
        {STEPS[activeStep]?.description}
      </Typography>

      {/* Navigation buttons */}
      <Divider sx={{ mb: 1.5 }} />
      <WizardNavBar
        onBack={activeStep > 0 ? handleBack : null}
        onNext={navConfig.onNextOverride || handleNext}
        onSkip={navConfig.showSkip ? (navConfig.onSkipOverride || handleNext) : null}
        nextDisabled={navConfig.nextDisabled}
        backDisabled={navConfig.backDisabled}
        nextLabel={navConfig.nextLabel || 'Continue'}
        skipLabel={navConfig.skipLabel || 'Skip'}
      />
      </Paper>

      {/* ── Step content ──────────────────────────────────────────────────────── */}
      <Paper sx={{ width: '100%', p: isWideLayoutStep ? { xs: 2, md: 3 } : { xs: 2, md: 4 } }}>
        {renderStep()}
      </Paper>

      {/* ── AI Cost summary — below all step content ──────────────────────────── */}
      {session?.uniqueId && (
        <AiCostSummaryPanel sessionId={session.uniqueId} refreshKey={costRefreshKey} />
      )}
    </Container>
  );
};

export default AlbumWizardPage;
